import { generateToDocFn } from '@dadohr/shared/domainTypes/document';
import { ITargetAudience } from '@dadohr/shared/domainTypes/targetAudience';
import { UserField } from '@dadohr/shared/domainTypes/experience';
import { Rule } from '@dadohr/shared/domainTypes/rules';
import { FS } from '@dadohr/shared/versions';
import {
  CollectionListener,
  createCollectionListenerStore,
  useCollectionListener
} from '../firecache/collectionListener';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../firecache/documentListener';
import { store } from '../firebase';
import { callFirebaseFunction } from '../firebaseFunctions';

const toTargetAudienceDoc = generateToDocFn<ITargetAudience>();

const targetAudiencesByOrgStore = createCollectionListenerStore(
  (orgId: string) => {
    return new CollectionListener(
      store().collection(FS.targetAudiences).where('orgId', '==', orgId),
      toTargetAudienceDoc
    );
  }
);

// Same [value, loading, error] shape as useAllUserSavedViews — see
// apps/src/services/savedView/index.ts. Do not add a live per-audience
// match-count to this list — see the plan's Regression safety section
// (computing a match count against the full employee directory for every
// row on every render is a performance trap at scale).
export const useAllTargetAudiences = (orgId: string) => {
  return useCollectionListener(targetAudiencesByOrgStore(orgId));
};

const targetAudienceByIdStore = createDocumentListenerGetter(
  (id) => store().collection(FS.targetAudiences).doc(id),
  toTargetAudienceDoc
);

export const useTargetAudienceById = (id: string) => {
  return useDocumentListener(targetAudienceByIdStore(id));
};

export type CreateTargetAudienceParams = {
  orgId: string;
  name: string;
  rule: Rule<UserField>;
};

export const createTargetAudience = (params: CreateTargetAudienceParams) =>
  callFirebaseFunction<{ id: string }>('targetAudience_create', params);

export type UpdateTargetAudienceParams = {
  id: string;
  name: string;
  rule: Rule<UserField>;
};

export const updateTargetAudience = (params: UpdateTargetAudienceParams) =>
  callFirebaseFunction<{ id: string }>('targetAudience_update', params);

export const deleteTargetAudience = (id: string) =>
  callFirebaseFunction<{ id: string }>('targetAudience_delete', { id });
